// src/utils/accessChecker.js

import { 
    hasSpecialAccess, 
    getSpecialAccessExpiry, 
    clearSpecialAccess, 
    getSpecialAccessHoursRemaining,
    updateSpecialAccessDisplayName
} from '../services/specialAccessService.js';

/**
 * Helper utility to check special access status on app startup
 * and display appropriate notifications
 */
export function checkSpecialAccessOnStartup() {
    // Skip if not in browser context
    if (typeof window === 'undefined' || !window.showGeneralMessage) return;
    
    try {
        const hasAccess = hasSpecialAccess();
        
        if (hasAccess) {
            const hoursRemaining = getSpecialAccessHoursRemaining();
            
            // If hours remaining is 0 but hasAccess still returned true,
            // we need to clear it as it may be just about to expire
            if (hoursRemaining <= 0) {
                clearSpecialAccess();
                // Force reload since we've lost access rights
                window.location.reload();
                return;
            }
            
            // Show appropriate notification based on time remaining
            if (hoursRemaining <= 1) {
                // Critical - less than 1 hour
                window.showGeneralMessage(
                    `⚠️ Special access expires in less than 1 hour!`, 
                    5000, true
                );
            } else if (hoursRemaining <= 4) {
                // Warning - less than 4 hours
                window.showGeneralMessage(
                    `Special access: ${hoursRemaining} hours remaining`, 
                    4000, false
                );
            } else if (hoursRemaining <= 24) {
                // Info - less than a day
                window.showGeneralMessage(
                    `Special access active: ${hoursRemaining} hours remaining`, 
                    3000, false
                );
            }
            
            // Update the username label to show hours remaining
            const usernameLabel = document.getElementById("ribbon-username-label");
            if (usernameLabel) {
                usernameLabel.textContent = `Guest+ (${hoursRemaining}h)`;
            }
            
            // Setup periodic checks for expiration
            setupExpiryCheck();
        }
    } catch (error) {
        console.error("Error in checkSpecialAccessOnStartup:", error);
    }
}

/**
 * Sets up periodic checks for access expiry
 */
function setupExpiryCheck() {
    // Check every 5 minutes
    const CHECK_INTERVAL = 5 * 60 * 1000;
    
    // Clear any existing interval
    if (window.specialAccessCheckInterval) {
        clearInterval(window.specialAccessCheckInterval);
    }
    
    // Set up new interval
    window.specialAccessCheckInterval = setInterval(() => {
        try {
            // Update the display name to reflect current remaining time
            updateSpecialAccessDisplayName();
            
            const hasAccess = hasSpecialAccess();
            
            if (!hasAccess) {
                // Access expired during current session
                window.showGeneralMessage("Special access has expired", 5000, true);
                
                // Clear interval
                clearInterval(window.specialAccessCheckInterval);
                window.specialAccessCheckInterval = null;
                
                // Force auth state recheck to update UI/scene
                window.dispatchEvent(new CustomEvent('special-access-expired'));
                
                // Delay reload to ensure the user sees the message
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                // Still has access, update hours in username label
                const hoursRemaining = getSpecialAccessHoursRemaining();
                const usernameLabel = document.getElementById("ribbon-username-label");
                if (usernameLabel && usernameLabel.textContent.includes('Guest+')) {
                    usernameLabel.textContent = `Guest+ (${hoursRemaining}h)`;
                }
                
                // Show warning message if expiry is approaching
                if (hoursRemaining <= 1) {
                    window.showGeneralMessage(
                        `⚠️ Special access expires in less than 1 hour!`, 
                        5000, true
                    );
                }
            }
        } catch (error) {
            console.error("Error in access expiry check:", error);
        }
    }, CHECK_INTERVAL);
    
    // Also check when visibility changes (tab becomes active)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // If tab becomes visible, check access status
            const hasAccess = hasSpecialAccess();
            if (!hasAccess && window.specialAccessCheckInterval) {
                // Access expired while tab was inactive
                window.showGeneralMessage("Special access has expired", 5000, true);
                clearInterval(window.specialAccessCheckInterval);
                window.specialAccessCheckInterval = null;
                
                // Force reload after a short delay
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else if (hasAccess) {
                // Update display name with current time remaining
                updateSpecialAccessDisplayName();
                
                // Update label in UI
                const hoursRemaining = getSpecialAccessHoursRemaining();
                const usernameLabel = document.getElementById("ribbon-username-label");
                if (usernameLabel && usernameLabel.textContent.includes('Guest+')) {
                    usernameLabel.textContent = `Guest+ (${hoursRemaining}h)`;
                }
            }
        }
    });
}

// Set up event listeners for special access events
if (typeof window !== 'undefined') {
    // Listen for granted event
    // In accessChecker.js, find the window event listener for 'special-access-granted':
window.addEventListener('special-access-granted', () => {
    console.log("Special access granted event detected");
    
    // Stop any ongoing scene transitions
    if (window.fixTransitionState) {
        window.fixTransitionState();
    }
    
    // Force auth state check
    window.dispatchEvent(new Event('auth-change'));
    
    // If we're in any guest menu scene, transition to authenticated menu
    const currentScene = window.gameInstance?.getActiveScene?.();
    const guestMenuScenes = ['MainMenuScene', 'MainMenuSceneDOM', 'GuestMenuScene', 'GuestMenuSceneDOM', 'MainMenuSceneUniversal'];
    
    if (currentScene && guestMenuScenes.includes(currentScene.scene.key)) {
        console.log("Transitioning to authenticated menu after special access granted");
        
        // CRITICAL FIX: Use feature flag routing for special access grant
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        const targetScene = useDOMAuthMenu ? 'AuthenticatedMenuSceneDOM' : 'AuthenticatedMenuScene';
        
        if (typeof currentScene.scene.start === 'function') {
            currentScene.scene.start(targetScene);
        }
    }
});
    
    // Listen for expiry events
    window.addEventListener('special-access-expired', () => {
        console.log("Special access expired event detected");
        // Force auth state check
        window.dispatchEvent(new Event('auth-change'));
        
        // If in any authenticated menu scene, force back to guest menu
        const currentScene = window.gameInstance?.getActiveScene?.();
        const authMenuScenes = ['AuthenticatedMenuScene', 'AuthenticatedMenuSceneDOM'];
        
        if (currentScene && authMenuScenes.includes(currentScene.scene.key)) {
            if (window.fixTransitionState) {
                window.fixTransitionState();
            }
            
            // CRITICAL FIX: Use feature flag routing for access expiry
            const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
            const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
            const targetScene = useUniversalLanding ? 'MainMenuSceneUniversal' :
                               (useDOMMenus ? 'GuestMenuSceneDOM' : 'GuestMenuScene');
            currentScene.scene.start(targetScene);
        }
    });
    
    // Listen for clearing events
    window.addEventListener('special-access-cleared', () => {
        console.log("Special access cleared event detected");
        // Clear any expiry checking interval
        if (window.specialAccessCheckInterval) {
            clearInterval(window.specialAccessCheckInterval);
            window.specialAccessCheckInterval = null;
        }
        window.dispatchEvent(new Event('auth-change'));
    });
}

export default { 
    checkSpecialAccessOnStartup 
};